/**
 * TableViewPanel — Context-aware DB Table Viewer
 * RIGHT side panel, minimizable, auto-detects screen → relevant tables
 * Shows latest 2-5 records by default, multi-table selection
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, Database, Minus, Maximize2, Plus } from 'lucide-react';
import { API_BASE as API_URL, getToken} from '../lib/api';


// Screen → relevant tables mapping
const SCREEN_TABLE_MAP = {
  GENERAL_LEDGER: ['M_GeneralLedger', 'T_AccountDetails'],
  PRIMARY_GROUP: ['M_PrimaryGroup'],
  GROUP_LEDGER: ['M_GroupLedger', 'M_PrimaryGroup'],
  SCHEDULE_MASTER: ['BSSchedule'],
  CASH_RECEIPT: ['M_CashReceiptVoucher', 'T_CashReceiptVoucher', 'T_AccountDetails'],
  BANK_RECEIPT: ['M_BankReceiptVoucher', 'T_BankReceiptVoucher', 'T_AccountDetails'],
  CASH_PAYMENT: ['M_CashPaymentVoucher', 'T_CashPaymentVoucher', 'T_AccountDetails'],
  BANK_PAYMENT: ['M_BankPaymentVoucher', 'T_BankPaymentVoucher', 'T_AccountDetails'],
  JOURNAL_ENTRY: ['M_JournalVoucher', 'T_JournalVoucher', 'T_AccountDetails'],
  SUPPLIER_MASTER: ['VendorMaster', 'VendAddinfo', 'Vendor_ProductDetails'],
  CUSTOMER_MASTER: ['CustomerMaster'],
  TRIAL_BALANCE: ['T_AccountDetails', 'M_GeneralLedger'],
  OPENING_BALANCE: ['M_GeneralLedger'],
  BALANCE_SHEET: ['M_GeneralLedger', 'M_PrimaryGroup'],
  PROFIT_LOSS: ['M_GeneralLedger', 'M_PrimaryGroup'],
};

const ALL_TABLES = ['T_AccountDetails', 'M_GeneralLedger', 'M_GroupLedger', 'M_PrimaryGroup', 'BSSchedule', 'M_CashReceiptVoucher', 'T_CashReceiptVoucher', 'M_CashPaymentVoucher', 'T_CashPaymentVoucher', 'M_BankReceiptVoucher', 'T_BankReceiptVoucher', 'M_BankPaymentVoucher', 'T_BankPaymentVoucher', 'M_JournalVoucher', 'T_JournalVoucher', 'VendorMaster', 'CustomerMaster', 'VendAddinfo', 'Vendor_ProductDetails', 'BranchMaster', 'ErrorInfo', 'ItemMaster'];

const TableViewPanel = ({ isOpen, onToggle, theme = 'light', activeModuleId }) => {
  const isDark = theme === 'dark';
  const [minimized, setMinimized] = useState(false);
  const [selectedTables, setSelectedTables] = useState([]);
  const [tableData, setTableData] = useState({});
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [dbInfo, setDbInfo] = useState(null);
  const token = getToken() || '';

  // Fetch DB connection info
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/db-info`);
        const j = await r.json();
        if (j.success) setDbInfo(j);
      } catch {}
    })();
  }, []);

  // Auto-select tables based on active screen
  useEffect(() => {
    if (!activeModuleId) return;
    const mapped = SCREEN_TABLE_MAP[activeModuleId] || [];
    if (mapped.length > 0) setSelectedTables(mapped);
  }, [activeModuleId]);

  const loadData = useCallback(async () => {
    if (!token || !isOpen || selectedTables.length === 0) return;
    setLoading(true);
    const results = {};
    for (const t of selectedTables) {
      try {
        const r = await fetch(`${API_URL}/api/system/table-view?table=${t}&limit=${limit}`, { headers: { Authorization: `Bearer ${token}` } });
        const j = await r.json();
        if (j.success) results[t] = { data: j.data || [], columns: j.columns || [], total: j.total || 0 };
      } catch { results[t] = { data: [], columns: [], total: 0 }; }
    }
    setTableData(results);
    setLoading(false);
  }, [token, isOpen, selectedTables, limit]);

  useEffect(() => { if (isOpen && !minimized) loadData(); }, [isOpen, minimized, loadData]);

  const toggleTable = (t) => setSelectedTables(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  if (!isOpen) return null;

  // Minimized: just a small tab
  if (minimized) {
    return (
      <div style={{ position: 'fixed', right: 0, bottom: 42, zIndex: 40, background: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#333' : '#ddd'), borderRadius: '4px 0 0 4px', padding: '4px 8px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} onClick={() => setMinimized(false)} data-testid="tableview-minimized">
        <Database size={14} className="text-blue-500" style={{ display: 'inline', marginRight: 4 }} />
        <span style={{ fontSize: 10, fontWeight: 700 }}>Tables ({selectedTables.length})</span>
      </div>
    );
  }

  const bg = isDark ? '#111' : '#fff';
  const border = isDark ? '#333' : '#e2e8f0';

  return (
    <div style={{ position: 'fixed', right: 0, top: 48, bottom: 40, width: 520, background: bg, borderLeft: `1px solid ${border}`, zIndex: 40, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 16px rgba(0,0,0,0.08)' }} data-testid="table-view-panel">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderBottom: `1px solid ${border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Database size={14} className="text-blue-500" />
          <span style={{ fontWeight: 700, fontSize: 12 }}>Table Viewer</span>
          <span style={{ fontSize: 9, color: '#999' }}>{activeModuleId || 'Global'}</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={loadData} style={{ padding: 2, border: 'none', background: 'none', cursor: 'pointer' }} title="Refresh"><RefreshCw size={13} className={loading ? 'animate-spin' : ''} style={{ color: '#666' }} /></button>
          <button onClick={() => setMinimized(true)} style={{ padding: 2, border: 'none', background: 'none', cursor: 'pointer' }} title="Minimize"><Minus size={13} style={{ color: '#666' }} /></button>
          <button onClick={onToggle} style={{ padding: 2, border: 'none', background: 'none', cursor: 'pointer' }} title="Close"><X size={13} style={{ color: '#e74c3c' }} /></button>
        </div>
      </div>

      {/* DB Connection Info */}
      {dbInfo && (
        <div style={{ padding: '4px 10px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 10, background: isDark ? '#0e0e0e' : '#f8fafc' }} data-testid="db-info-strip">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: dbInfo.primary?.connected ? '#22c55e' : '#ef4444', flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'var(--erp-font-mono, monospace)', color: dbInfo.primary?.connected ? (isDark ? '#4ade80' : '#16a34a') : '#ef4444' }}>{dbInfo.primary?.database || '-'}</span>
            <span style={{ fontSize: 9, color: isDark ? '#555' : '#94a3b8' }}>@</span>
            <span style={{ fontSize: 9, fontFamily: 'var(--erp-font-mono, monospace)', color: isDark ? '#666' : '#64748b' }}>{dbInfo.primary?.server}:{dbInfo.primary?.port}</span>
          </div>
          {dbInfo.tenant?.connected && (
            <>
              <div style={{ width: 1, height: 12, background: isDark ? '#333' : '#cbd5e1' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'var(--erp-font-mono, monospace)', color: isDark ? '#60a5fa' : '#2563eb' }}>{dbInfo.tenant?.database}</span>
                <span style={{ fontSize: 9, color: isDark ? '#555' : '#94a3b8' }}>@</span>
                <span style={{ fontSize: 9, fontFamily: 'var(--erp-font-mono, monospace)', color: isDark ? '#666' : '#64748b' }}>{dbInfo.tenant?.server}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Table selector + limit */}
      <div style={{ padding: '4px 10px', borderBottom: `1px solid ${border}`, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: '#666' }}>Rows:</span>
        {[2, 5, 10, 20, 50].map(n => (
          <button key={n} onClick={() => setLimit(n)} style={{ fontSize: 9, padding: '1px 6px', border: `1px solid ${limit === n ? '#4A90D9' : border}`, borderRadius: 3, background: limit === n ? '#EBF3FC' : 'transparent', cursor: 'pointer', fontWeight: limit === n ? 700 : 400, color: limit === n ? '#4A90D9' : '#666' }}>{n}</button>
        ))}
        <div style={{ marginLeft: 'auto', position: 'relative' }}>
          <select onChange={e => { if (e.target.value && !selectedTables.includes(e.target.value)) setSelectedTables(p => [...p, e.target.value]); e.target.value = ''; }} style={{ fontSize: 10, padding: '2px 4px', border: `1px solid ${border}`, borderRadius: 3, width: 130 }}>
            <option value="">+ Add Table</option>
            {ALL_TABLES.filter(t => !selectedTables.includes(t)).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Data sections — one per selected table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {selectedTables.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 12 }}>No tables selected. Navigate to a screen or add tables manually.</div>
        ) : selectedTables.map(t => {
          const td = tableData[t] || { data: [], columns: [], total: 0 };
          return (
            <div key={t} style={{ borderBottom: `2px solid ${border}` }}>
              {/* Table header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', background: isDark ? '#1a1a1a' : '#f0f5fa', borderBottom: `1px solid ${border}` }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#4A90D9' }}>{t}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 9, color: '#999' }}>{td.data.length}/{td.total} rows</span>
                  <button onClick={() => toggleTable(t)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}><X size={11} style={{ color: '#e74c3c' }} /></button>
                </div>
              </div>
              {/* Table data */}
              {loading ? <div style={{ padding: 8, textAlign: 'center', fontSize: 10, color: '#999' }}>Loading...</div>
              : td.data.length === 0 ? <div style={{ padding: 8, textAlign: 'center', fontSize: 10, color: '#999' }}>Empty</div>
              : (
                <div style={{ overflow: 'auto', maxHeight: 200 }}>
                  <table style={{ fontSize: 9, borderCollapse: 'collapse', width: '100%' }}>
                    <thead><tr>{td.columns.map(c => <th key={c} style={{ padding: '3px 4px', borderBottom: '1px solid ' + border, background: isDark ? '#161616' : '#f8f9fb', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 700, position: 'sticky', top: 0, fontSize: 8 }}>{c}</th>)}</tr></thead>
                    <tbody>{td.data.map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : (isDark ? '#0e0e0e' : '#fafbfc') }}>
                        {td.columns.map(c => <td key={c} style={{ padding: '2px 4px', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderBottom: '1px solid ' + (isDark ? '#1a1a1a' : '#f0f0f0'), fontFamily: typeof row[c] === 'number' ? 'monospace' : 'inherit', fontSize: 8 }} title={String(row[c] ?? '')}>{row[c] !== null && row[c] !== undefined ? String(row[c]).substring(0, 40) : <span style={{ color: '#ccc' }}>-</span>}</td>)}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TableViewPanel;
